import { _support, validateOption, logger, isBrowserEnv, isWxMiniEnv, Queue, isEmpty, Severity, generateUUID, getLocationHref, getTimestamp, compress, getDeviceInfo } from '@tr-infra/pilot-utils'
import { SDK_NAME, SDK_VERSION, ENV_DSN } from '@tr-infra/pilot-shared'
import { ReportResourceType, EMethods, InitOptions, LogType, ReportDataType, FinalReportModel } from '@tr-infra/pilot-types'
import sessionManager from './session'
/**
 * 用来传输数据类，包含img标签、xhr请求
 * 功能：支持img请求和xhr请求、可以断点续存（保存在localstorage），
 * 待开发：目前不需要断点续存，因为接口不是很多，只有错误时才触发，如果接口太多可以考虑合并接口、
 *
 * ../class Transport
 */
export class TransportData {
  queue: Queue
  beforeDataReport: unknown = null
  backTrackerId: unknown = null
  configReportXhr: unknown = null
  configReportUrl: unknown = null
  configReportWxRequest: unknown = null
  useImgUpload = false
  app_id = ''
  user_id = ''
  env = ''
  trackKey = ''
  errorDsn = ''
  trackDsn = ''
  cachePool: Array<LogType> = []
  maxCacheSize = 10
  cacheInterval = 10000
  lastTransportTime = 0
  timer: number = null
  constructor() {
    this.queue = new Queue()
  }

  /**
   * options 初始化
   * @param options 
   */
  bindOptions(options: InitOptions = {}): void {
    const {
      app_id, 
      user_id,
      env,
      domainProxy,
      beforeDataReport,
      configReportXhr,
      backTrackerId,
      trackDsn,
      trackKey,
      configReportUrl,
      useImgUpload,
      configReportWxRequest,
      maxCacheSize,
      cacheInterval
    } = options
    validateOption(app_id, 'app_id', 'string') && (this.app_id = app_id)
    validateOption(user_id, 'user_id', 'string') && (this.user_id = user_id)
    validateOption(trackKey, 'trackKey', 'string') && (this.trackKey = trackKey)
    validateOption(env, 'env', 'string') && (this.errorDsn = ENV_DSN[env.toUpperCase()], this.env = env)
    validateOption(trackDsn, 'trackDsn', 'string') && (this.trackDsn = trackDsn)
    validateOption(useImgUpload, 'useImgUpload', 'boolean') && (this.useImgUpload = useImgUpload)
    validateOption(beforeDataReport, 'beforeDataReport', 'function') && (this.beforeDataReport = beforeDataReport)
    validateOption(configReportXhr, 'configReportXhr', 'function') && (this.configReportXhr = configReportXhr)
    validateOption(backTrackerId, 'backTrackerId', 'function') && (this.backTrackerId = backTrackerId)
    validateOption(configReportUrl, 'configReportUrl', 'function') && (this.configReportUrl = configReportUrl)
    validateOption(configReportWxRequest, 'configReportWxRequest', 'function') && (this.configReportWxRequest = configReportWxRequest)
    validateOption(maxCacheSize, 'maxCacheSize', 'number') && (this.maxCacheSize = maxCacheSize)
    validateOption(cacheInterval, 'cacheInterval', 'number') && (this.cacheInterval = cacheInterval)
    if (validateOption(domainProxy, 'domainProxy', 'string')) {
      // 判断是否是完整地址
      if (domainProxy.indexOf('http') === 0) {
        this.errorDsn = domainProxy
      } else {
        this.errorDsn = 'https://' + domainProxy + '/tango/data/logs'
      }
    }
  }

  /**
   * image 方式上报
   * @param data 
   * @param url 
   */
  imgRequest(data: any, url: string): void {
    const requestFun = () => {
      let img = new Image()
      const spliceStr = url.indexOf('?') === -1 ? '?' : '&'
      img.src = `${url}${spliceStr}data=${encodeURIComponent(JSON.stringify(data))}`
      img = null
    }
    this.queue.addFn(requestFun)
  }

  /**
   * xhr 上报
   * @param data 
   * @param url 
   */
  async xhrPost(data: any, url: string) {
    const requestFun = (): void => {
      const xhr = new XMLHttpRequest()
      xhr.open(EMethods.Post, url)
      xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8')
      xhr.setRequestHeader('X-Scenario-Type', 'h5')
      xhr.setRequestHeader('X-Tr-Devtype', 'h5')
      xhr.withCredentials = true
      if (typeof this.configReportXhr === 'function') {
        this.configReportXhr(xhr, data)
      }
      xhr.send(JSON.stringify(data))
    }
    this.queue.addFn(requestFun)
  }

  /**
   * 微信小程序上报方式
   * @param data 
   * @param url 
   */
  async wxPost(data: any, url: string) {
    const requestFun = (): void => {
      let requestOptions = { method: 'POST' } as WechatMiniprogram.RequestOption
      if (typeof this.configReportWxRequest === 'function') {
        const params = this.configReportWxRequest(data)
        // default method
        requestOptions = { ...requestOptions, ...params }
      }
      requestOptions = {
        ...requestOptions,
        data: JSON.stringify(data),
        url
      }
      wx.request(requestOptions)
    }
    this.queue.addFn(requestFun)
  }
  /**
   * 获取 report resource
   * @returns ReportResourceType
   */
  getResource(): ReportResourceType {
    return {
      app_id: this.app_id,
      uuid: generateUUID(),
      url: getLocationHref(),
      s_v: SDK_VERSION,
      s_n: SDK_NAME,
      lang: navigator.language,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      refer: document.referrer,
      uts: getTimestamp(),
      ...getDeviceInfo()
    }
  }

  /**
   * 上报数据格式
   * @param logs
   * @returns FinalReportModel
   */
  getTransportData(logs: LogType[]): FinalReportModel {
    return {
      logs,
      resource: this.getResource(),
      env: 'prod',
      user: this.user_id,
    }
  }

  /**
   * 判断是否 sdk url
   * @param targetUrl
   * @returns boolean
   */
  isSdkTransportUrl(targetUrl: string): boolean {
    let isSdkDsn = false
    if (this.errorDsn && targetUrl.indexOf(this.errorDsn) !== -1) {
      isSdkDsn = true
    }
    if (this.trackDsn && targetUrl.indexOf(this.trackDsn) !== -1) {
      isSdkDsn = true
    }
    return isSdkDsn
  }

  /**
   * 监控错误上报的请求函数
   * @param data 错误上报数据格式
   * @returns
   */
  async send(data: ReportDataType) {
    const dsn = this.errorDsn
    if (isEmpty(this.app_id)) {
      return logger.error('app_id 不能为空，请在初始化中配置')
    }
    if (isEmpty(dsn)) {
      return logger.error('env 不能为空，请在初始化中配置')
    }
    // 所有上报日志先进缓存池
    this.cache(data)
  }

  /**
   * 由于浏览器最大连接数限制，采用批量上报方式
   * 批量上报、本地缓存
   * 定时、数量满足其一则触发上报
   */
  cache(log: ReportDataType) {
    const _attrs = { 
      ...log,
      sessionId: sessionManager.getSessionId()
    }
    delete _attrs.elapsedTime
    delete _attrs.sTime
    delete _attrs.eTime
    delete _attrs.level
    const logItem: LogType = {
      user: this.user_id,
      type: 'log',
      level: log.level,
      attributes: _attrs,
      timestamp: _attrs.time
    }
    this.cachePool.push(logItem)
    const now = getTimestamp()
    const report = () => {
      const logs = this.cachePool.slice(0)
      this.upload(logs)
      this.cachePool = []
      this.lastTransportTime = now
      clearTimeout(this.timer)
      this.timer = null
    }
    // 满足最大缓存数立即上报 || 错误日志立即上报
    if (this.cachePool.length >= this.maxCacheSize || log.level === Severity.Error) {
      report()
    }
    // 定时上报
    if (!this.timer) {
      this.timer = window.setTimeout(() => {
        if (this.cachePool.length) {
          report()
        }
      }, this.cacheInterval)
    }
  }

  /**
   * 上报
   * @param logs 
   * @returns 
   */
  upload(logs: LogType[]): void {
    const dsn = this.errorDsn
    const result = this.getTransportData(logs)
    const lzStr = compress(JSON.stringify(result))
    if (isBrowserEnv) {
     this.useImgUpload ? this.imgRequest(lzStr, dsn) : this.xhrPost(lzStr, dsn)
    }
    if (isWxMiniEnv) {
     this.wxPost(result, dsn)
    }
  }
}
const transportData = _support.transportData || (_support.transportData = new TransportData())
export { transportData }
